import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { Home, Users, ShoppingCart, Activity, User, BellRing } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import InviteModal from './InviteModal';
import { Button } from '@/components/ui/button';
import BackgroundPattern from './BackgroundPattern';
import { Badge } from '@/components/ui/badge';
import { 
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface LayoutProps {
  children: React.ReactNode;
  className?: string;
}

const Layout: React.FC<LayoutProps> = ({ children, className }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState([
    { id: 1, message: "You received a $5 micro-gift from John", read: false },
    { id: 2, message: "You received a $10 micro-gift from Sarah", read: false },
    { id: 3, message: "You received a $15 micro-gift from Mike", read: false },
  ]);
  const [showAll, setShowAll] = useState(false);
  
  const unreadCount = notifications.filter(n => !n.read).length;
  
  const markAsRead = (id: number) => {
    setNotifications(prevNotifications => 
      prevNotifications.map(notif => 
        notif.id === id ? { ...notif, read: true } : notif
      )
    );
  };
  
  const markAllAsRead = () => {
    setNotifications(prevNotifications => 
      prevNotifications.map(notif => ({ ...notif, read: true }))
    );
  };
  
  const notificationsToShow = showAll ? notifications : notifications.slice(0, 3);
  
  const goToHome = () => {
    navigate('/');
    const indexPage = document.getElementById('index-page');
    if (indexPage) {
      const event = new CustomEvent('reset-to-welcome');
      indexPage.dispatchEvent(event);
    }
  };
  
  return (
    <div className={cn("min-h-screen flex flex-col relative overflow-hidden", className)}>
      <BackgroundPattern />
      
      <header className={cn(
        "py-4 border-b bg-white/90 backdrop-blur-sm z-10 sticky top-0",
        "w-full"
      )}>
        <div className="container flex justify-between items-center">
          <div className="flex items-center space-x-2 flex-1">
            <Button 
              variant="ghost" 
              className={cn(
                "flex items-center gap-2 text-muted-foreground hover:text-givgo-purple transition-colors p-0",
                location.pathname === "/" && "text-givgo-purple"
              )}
              onClick={goToHome}
            >
              <Home size={18} />
              <span className="text-sm">Home</span>
            </Button>
            
            <Link to="/market">
              <Button 
                variant="ghost" 
                className={cn(
                  "flex items-center gap-2 text-muted-foreground hover:text-givgo-purple transition-colors p-0",
                  location.pathname === "/market" && "text-givgo-purple"
                )}
              >
                <ShoppingCart size={18} />
                <span className="text-sm">Market</span>
              </Button>
            </Link>
            
            <Link to="/activity">
              <Button 
                variant="ghost" 
                className={cn(
                  "flex items-center gap-2 text-muted-foreground hover:text-givgo-purple transition-colors p-0",
                  location.pathname === "/activity" && "text-givgo-purple"
                )}
              >
                <Activity size={18} />
                <span className="text-sm">Activity</span>
              </Button>
            </Link>
            
            <Link to="/invite">
              <Button 
                variant="ghost" 
                className={cn(
                  "flex items-center gap-2 text-muted-foreground hover:text-givgo-purple transition-colors p-0",
                  location.pathname === "/invite" && "text-givgo-purple"
                )}
              >
                <Users size={18} />
                <span className="text-sm">Contacts</span>
              </Button>
            </Link>
            
            <InviteModal trigger={
              <Button variant="ghost" className="flex items-center gap-2 text-muted-foreground hover:text-givgo-purple transition-colors p-0">
                <Users size={18} />
                <span className="text-sm">Invite</span>
              </Button>
            } />
          </div>
          
          <div className="flex-1 flex justify-center">
            <Link to="/" className="flex items-center justify-center" onClick={goToHome}>
              <div className="h-10 w-10 rounded-full overflow-hidden mx-auto">
                <img 
                  src="/lovable-uploads/00c921fe-bb3a-4eb5-ad50-76a967cd91c7.png" 
                  alt="GivGo Logo" 
                  className="h-10 w-10 object-cover rounded-full"
                />
              </div>
            </Link>
          </div>
          
          <div className="flex-1 flex justify-end space-x-4 items-center">
            <Popover>
              <PopoverTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="relative flex items-center gap-2 text-muted-foreground hover:text-givgo-purple transition-colors p-0"
                >
                  <BellRing size={18} />
                  {unreadCount > 0 && (
                    <Badge 
                      className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-1.5 rounded-full"
                    >
                      {unreadCount}
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0" align="end">
                <div className="p-4 bg-givgo-purple text-white flex justify-between items-center">
                  <h3 className="font-medium">Notifications</h3>
                  {unreadCount > 0 && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-auto py-1 text-white hover:text-white/80"
                      onClick={markAllAsRead}
                    >
                      Mark all as read
                    </Button>
                  )}
                </div>
                <div className="max-h-[300px] overflow-y-auto">
                  {notificationsToShow.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground">
                      No new notifications
                    </div>
                  ) : (
                    notificationsToShow.map((notification) => (
                      <div 
                        key={notification.id} 
                        className={cn(
                          "p-3 border-b last:border-b-0 hover:bg-muted/50 cursor-pointer",
                          !notification.read && "bg-blue-50"
                        )}
                        onClick={() => markAsRead(notification.id)}
                      >
                        <div className="flex items-start gap-2">
                          <div className="flex-shrink-0 mt-0.5">
                            <div className={cn(
                              "w-2 h-2 rounded-full",
                              notification.read ? "bg-muted" : "bg-blue-500"
                            )}></div>
                          </div>
                          <div>
                            <p className="text-sm">{notification.message}</p>
                            <p className="text-xs text-muted-foreground mt-1">Just now</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                {notifications.length > 3 && (
                  <div className="p-2 border-t text-center">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="w-full text-sm"
                      onClick={() => setShowAll(!showAll)}
                    >
                      {showAll ? "Show less" : `Show all (${notifications.length})`}
                    </Button>
                  </div>
                )}
              </PopoverContent>
            </Popover>
            
            <Link to="/profile">
              <Button 
                variant="ghost" 
                className={cn(
                  "flex items-center gap-2 text-muted-foreground hover:text-givgo-purple transition-colors p-0",
                  location.pathname === "/profile" && "text-givgo-purple"
                )}
              >
                <User size={18} />
                <span className="text-sm">Profile</span>
              </Button>
            </Link>
          </div>
        </div>
      </header>
      
      <main className="flex-1 container py-6 z-10 overflow-y-auto">
        {children}
      </main>
      
      <footer className="py-4 text-center text-sm text-muted-foreground border-t bg-white/90 backdrop-blur-sm z-10">
        <div className="container">
          <div className="mb-1">GivGo - Micro-Gifting Platform © {new Date().getFullYear()}</div>
          <div className="text-givgo-purple font-medium italic">"GivGo, when a like is not enough"</div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
